import React from 'react';

import { TextField } from '@mui/material';
import _ from 'es-toolkit/compat';
import { NumericFormat } from 'react-number-format';

import { Nullable } from '@clearverify/types';

import { getFieldError, getFieldWarning, hasFieldError } from 'src/utils/finalForm';

import type { NumberFieldProps } from './interface';

export const NumberField: React.FC<NumberFieldProps> = ({
  input,
  meta,
  inputProps,
  className,
  disabled,
  warning,
  showErrorOnlyTouched = true,
  showErrorWhenDisabled = true,
  placeholder = '100',
  ...restProps
}) => {
  const { name, value, onBlur, onChange, focused } = input;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    event => {
      const value = event.target.value as Nullable<string>;
      onChange(value ? Number(value) : null);
    },
    [onChange],
  );

  const hasStartAdornment = Boolean(inputProps?.startAdornment);
  const hasEndAdornment = Boolean(inputProps?.endAdornment);
  const hasAnyAdornment = hasStartAdornment || hasEndAdornment;
  const hasValue = !_.isNil(value) && !_.isNaN(value);
  const shrink = hasValue || hasAnyAdornment || focused;

  const error = hasFieldError(meta, showErrorOnlyTouched);
  const showError = showErrorWhenDisabled || !disabled;
  const helperText = showError
    ? getFieldError(meta, showErrorOnlyTouched) || getFieldWarning(value, meta, warning, disabled)
    : '';

  return (
    <NumericFormat
      valueIsNumericString
      className={className}
      name={name}
      value={value}
      customInput={TextField}
      variant="outlined"
      disabled={disabled}
      onChange={handleChange}
      onBlur={onBlur}
      focused={focused}
      error={showError ? error : false}
      helperText={helperText}
      placeholder={placeholder}
      {...restProps}
      slotProps={{
        input: inputProps,
        inputLabel: {
          shrink,
        },
      }}
    />
  );
};
